import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-icon';
import { ToastrService } from '../../services/toastr-service';
import { RepairCodeService } from '../../services/repair-code.service';
import { CreateRepairCodeRequest } from '../../models/create-repair-code-request.model';
import { RepairCodeDetailView } from '../../models/repair-code-detail-view.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { RepairCodeFormValue } from '../../form-model/create-repair-code.form.model';
import { RepairCodeInformationComponent } from '../../components/create-repair-code/repair-code-information/repair-code-information.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
    selector: 'app-create-repair-code',
    imports: [
        CommonModule, ReactiveFormsModule, RouterModule, ErrorComponent,
        NgxSkeletonLoaderModule, RepairCodeInformationComponent, MasterDataAuditLogComponent
    ],
    templateUrl: './create-repair-code.component.html',
    styleUrls: ['./create-repair-code.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateRepairCodeComponent implements OnInit, OnDestroy {
    formSubmitErrors = signal<string[]>([]);
    submitLoader = signal<boolean>(false);
    isEditMode = signal<boolean>(false);
    showCancelDialog = signal<boolean>(false);
    openAuditLog = false;
    repairMode: string | null = null;
    repairCode: string | null = null;
    repairCodeData = signal<RepairCodeDetailView | null>(null);

    private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    createRepairCodeForm = this.formBuilder.group({});

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly toastrService: ToastrService,
        private readonly repairCodeService: RepairCodeService
    ) {}

    ngOnInit(): void {
        void this.initialize();
    }

    private async initialize(): Promise<void> {
        this.repairMode = this.route.snapshot.paramMap.get('repairMode');
        this.repairCode = this.route.snapshot.paramMap.get('repairCode');
        this.addControlsToTheFormGroup();
        if (this.repairMode && this.repairCode) {
            this.isEditMode.set(true);
            await this.loadRepairCodeData(this.repairMode, this.repairCode);
        } else {
            this.isEditMode.set(false);
        }
    }

    private async loadRepairCodeData(repairMode: string, repairCode: string): Promise<void> {
        try {
            const data = await firstValueFrom(this.repairCodeService.getRepairCodeByModeAndCode(repairMode, repairCode));
            this.repairCodeData.set(data);
            this.populateFormWithData(data);
            this.createRepairCodeForm.get('repairMode')?.disable({ emitEvent: false });
            this.createRepairCodeForm.get('repairCode')?.disable({ emitEvent: false });
        } catch (err: any) {
            console.error('Error loading repair code data:', err);
            this.toastrService.showToastr('Failed to load repair code data', 'error');
            this.router.navigate(['/master-data/repair-code']);
        }
    }

    private populateFormWithData(data: RepairCodeDetailView | null): void {
        if (!data) return;
        this.createRepairCodeForm.patchValue({
            repairMode: data.repairMode,
            repairCode: data.repairCode,
            description: data.description,
            maxLabourHours: String(data.maxLabourHours),
            maxQuantity: String(data.maxQuantity),
            maxMaterialPrice: String(data.maxMaterialPrice),
            excludePreparationHours: data.excludePreparationHours,
            taxApplied: data.taxApplied,
            isActive: data.isActive,
            allowParts: data.allowParts,
            multipleEq: data.multipleEq
        });
        this.createRepairCodeForm.markAsPristine();
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    private addControlsToTheFormGroup(): void {
        this.createRepairCodeForm.addControl('repairMode', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
        this.createRepairCodeForm.addControl('repairCode', this.formBuilder.nonNullable.control<string>('', [
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(4)
        ]));
        this.createRepairCodeForm.addControl('description', this.formBuilder.nonNullable.control<string>('', [
            Validators.required,
            Validators.maxLength(100)
        ]));
        this.createRepairCodeForm.addControl('maxLabourHours', this.formBuilder.nonNullable.control<string>('0', [Validators.min(0)]));
        this.createRepairCodeForm.addControl('maxQuantity', this.formBuilder.nonNullable.control<string>('0', [Validators.min(0)]));
        this.createRepairCodeForm.addControl('maxMaterialPrice', this.formBuilder.nonNullable.control<string>('0', [Validators.min(0)]));
        this.createRepairCodeForm.addControl('excludePreparationHours', this.formBuilder.control<boolean>(false));
        this.createRepairCodeForm.addControl('taxApplied', this.formBuilder.control<boolean>(false));
        this.createRepairCodeForm.addControl('isActive', this.formBuilder.control<boolean>(false));
        this.createRepairCodeForm.addControl('allowParts', this.formBuilder.control<boolean>(false));
        this.createRepairCodeForm.addControl('multipleEq', this.formBuilder.control<boolean>(false));
    }

    isControlInErrorState(control: FormControl | AbstractControl | null | undefined): boolean | null {
        return control?.invalid && control?.touched ? true : null;
    }

    onCancel(): void {
        if (this.createRepairCodeForm.dirty) {
            this.showCancelDialog.set(true);
        } else {
            this.navigateToRepairCode();
        }
    }

    confirmCancel(): void {
        this.showCancelDialog.set(false);
        this.navigateToRepairCode();
    }

    keepEditing(): void {
        this.showCancelDialog.set(false);
    }

    private navigateToRepairCode(): void {
        if (this.isEditMode() && this.repairMode && this.repairCode) {
            this.router.navigate(['/master-data/repair-code', this.repairMode, this.repairCode]);
        } else {
            this.router.navigate(['/master-data/repair-code']);
        }
    }

    onSubmit(): void {
        if (this.createRepairCodeForm.invalid) {
            for (const key of Object.keys(this.createRepairCodeForm.controls)) {
                this.createRepairCodeForm.get(key)?.markAsTouched();
            }
            this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
            return;
        }
        if (this.isEditMode()) {
            this.updateRepairCode();
        } else {
            this.createRepairCode();
        }
    }

    private buildCreateRequest(): CreateRepairCodeRequest {
        const formValue = this.createRepairCodeForm.getRawValue() as RepairCodeFormValue;
        return {
            repairMode: formValue.repairMode,
            repairCode: formValue.repairCode,
            description: formValue.description,
            maxQuantity: Number(formValue.maxQuantity),
            maxMaterialPrice: Number(formValue.maxMaterialPrice),
            excludePreparationHours: formValue.excludePreparationHours ?? false,
            maxLabourHours: Number(formValue.maxLabourHours),
            taxApplied: formValue.taxApplied ?? false,
            isActive: formValue.isActive ?? false,
            allowParts: formValue.allowParts ?? false,
            multipleEq: formValue.multipleEq ?? false,
        };
    }

    private createRepairCode(): void {
        this.submitLoader.set(true);
        this.formSubmitErrors.set([]);
        const request = this.buildCreateRequest();
        this.repairCodeService.createRepairCode(request)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.submitLoader.set(false);
                    this.createRepairCodeForm.reset();
                    this.formSubmitErrors.set([]);
                    this.toastrService.showToastr(`Repair Code ${request.repairCode} added successfully`, 'success');
                    this.router.navigate(['/master-data/repair-code']);
                },
                error: (error: HttpErrorResponse) => {
                    this.handleRepairCodeError(error, 'creating');
                }
            });
    }

    private updateRepairCode(): void {
        this.submitLoader.set(true);
        this.formSubmitErrors.set([]);
        const formValue = this.createRepairCodeForm.getRawValue() as RepairCodeFormValue;
        const updatePayload = {
            description: formValue.description,
            maxQuantity: Number(formValue.maxQuantity),
            maxMaterialPrice: Number(formValue.maxMaterialPrice),
            excludePreparationHours: formValue.excludePreparationHours ?? false,
            maxLabourHours: Number(formValue.maxLabourHours),
            taxApplied: formValue.taxApplied ?? false,
            isActive: formValue.isActive ?? false,
            allowParts: formValue.allowParts ?? false,
            multipleEq: formValue.multipleEq ?? false,
        };
        this.repairCodeService.updateRepairCode(this.repairMode!, this.repairCode!, updatePayload)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.submitLoader.set(false);
                    this.formSubmitErrors.set([]);
                    this.toastrService.showToastr(`Repair Code ${this.repairCode} updated successfully`, 'success');
                    this.router.navigate(['/master-data/repair-code', this.repairMode, this.repairCode]);
                },
                error: (error: HttpErrorResponse) => {
                    this.handleRepairCodeError(error, 'updating');
                }
            });
    }

    private handleRepairCodeError(error: HttpErrorResponse, operation: string): void {
        this.submitLoader.set(false);
        if (!error) {
            this.formSubmitErrors.set([`Failed to ${operation} repair code. Please try again.`]);
            this.toastrService.showToastr(`Failed to ${operation} repair code. Please try again.`, 'error');
            return;
        }
        if (error.status === 400) {
            this.formSubmitErrors.set(['Invalid data. Please check your inputs.']);
            this.toastrService.showToastr('Invalid data. Please check your inputs.', 'error');
        } else if (error.status === 409) {
            this.formSubmitErrors.set(['This repair code already exists.']);
            this.toastrService.showToastr('This repair code already exists.', 'error');
        } else if (error.status === 422 && error.error?.violations) {
            const errors = error.error.violations.map((v: { message: string }) => v.message);
            this.formSubmitErrors.set(errors);
            this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
        } else {
            this.formSubmitErrors.set([`Failed to ${operation} repair code. Please try again.`]);
            this.toastrService.showToastr(`Failed to ${operation} repair code. Please try again.`, 'error');
        }
    }

    onAuditLogClick(): void {
        if (!this.repairMode || !this.repairCode) {
            this.toastrService.showToastr('Repair code keys not available for audit log', 'error');
            return;
        }
        this.openAuditLog = true;
    }

    readonly auditLogFetcher = (
        _transactionCode: string,
        isSystemLog: boolean,
        page: number,
        limit: number
    ) => this.repairCodeService.getRepairCodeAuditLogs(
        this.repairMode!,
        this.repairCode!,
        isSystemLog,
        page,
        limit
    );
}
